import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import { Plus, Trash2, Copy, GripVertical, CalendarClock, Link2 } from "lucide-react";
import { toast } from "sonner";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./ui/select";
import { Input } from "./ui/input";
import { Button } from "./ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "./ui/popover";
import ContentLinksDialog from "./ContentLinksDialog";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";

const GRID = "28px 1.3fr 1.3fr 170px 170px minmax(180px,1fr) 160px 120px";

export const CONTENT_STATUSES = ["Allocated", "Work in Progress", "Feedback", "Completed", "Live"];

const STATUS_META = {
  "Allocated":        { bg: "bg-slate-100",  text: "text-slate-600" },
  "Work in Progress": { bg: "bg-sky-100",    text: "text-sky-700" },
  "Feedback":         { bg: "bg-butter-100", text: "text-amber-700" },
  "Completed":        { bg: "bg-mint-100",   text: "text-emerald-700" },
  "Live":             { bg: "bg-lilac-100",  text: "text-purple-700" },
};

const isValidISODate = (v) => {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(v || "")) return false;
  const [y, m, d] = v.split("-").map(Number);
  return m >= 1 && m <= 12 && d >= 1 && d <= 31 && y >= 1900 && y <= 2999;
};

const fmtShortDate = (iso) => {
  if (!isValidISODate(iso)) return "";   // never render a malformed value
  const [, m, d] = iso.split("-").map(Number);
  return `${d}/${m}`;
};

// Timeline = start + end date range. Uses a local draft so typing into the native
// date input is never interrupted by the async save + re-render cycle.
function ContentDateCell({ row, onUpdate, testid }) {
  const [open, setOpen] = useState(false);
  const startProp = row.start_date || "";
  const endProp = row.end_date || "";
  const [start, setStart] = useState(startProp);
  const [end, setEnd] = useState(endProp);

  useEffect(() => {
    if (open) { setStart(startProp); setEnd(endProp); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const commit = (sRaw, eRaw) => {
    let s = isValidISODate(sRaw) ? sRaw : "";
    let e = isValidISODate(eRaw) ? eRaw : "";
    if (s && !e) e = s;
    if (s && e && e < s) { const t = s; s = e; e = t; }
    if (s === startProp && e === endProp) return;
    onUpdate(row.id, { start_date: s, end_date: e });
  };

  const clear = () => {
    setStart(""); setEnd(""); setOpen(false);
    if (startProp || endProp) onUpdate(row.id, { start_date: "", end_date: "" });
  };

  const startLbl = fmtShortDate(startProp);
  const endLbl = fmtShortDate(endProp);
  const label = startLbl
    ? (endLbl && endLbl !== startLbl ? `${startLbl} – ${endLbl}` : startLbl)
    : (endLbl || "Set dates");

  return (
    <Popover open={open} onOpenChange={(o) => { setOpen(o); if (!o) commit(start, end); }}>
      <PopoverTrigger asChild>
        <button
          className={`h-8 w-full px-2 rounded-md border text-xs text-left flex items-center gap-1.5 transition-colors ${
            startProp ? "border-slate-200 text-slate-700 bg-white" : "border-dashed border-slate-300 text-slate-400 hover:border-slate-400"
          }`}
          data-testid={testid}
        >
          <CalendarClock className="h-3.5 w-3.5 shrink-0 text-slate-400" />
          <span className="truncate">{label}</span>
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-60 p-3 space-y-3" align="start">
        <div className="space-y-1">
          <label className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">Start date</label>
          <input
            type="date"
            value={start}
            min="2000-01-01"
            max="2999-12-31"
            onChange={(e) => setStart(e.target.value)}
            onBlur={() => commit(start, end)}
            className="w-full h-9 px-2 rounded-md border border-slate-200 text-sm"
            data-testid={`${testid}-start`}
          />
        </div>
        <div className="space-y-1">
          <label className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">End date</label>
          <input
            type="date"
            value={end}
            min="2000-01-01"
            max="2999-12-31"
            onChange={(e) => setEnd(e.target.value)}
            onBlur={() => commit(start, end)}
            className="w-full h-9 px-2 rounded-md border border-slate-200 text-sm"
            data-testid={`${testid}-end`}
          />
        </div>
        {(start || startProp) && (
          <button onClick={clear} className="text-xs text-rose-600 hover:underline" data-testid={`${testid}-clear`}>
            Clear dates
          </button>
        )}
      </PopoverContent>
    </Popover>
  );
}

function ContentRow({ r, members, onUpdate, onDuplicate, onDelete, onOpenLinks }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: r.id });
  const style = { transform: CSS.Transform.toString(transform), transition, opacity: isDragging ? 0.5 : 1, gridTemplateColumns: GRID };
  const sMeta = STATUS_META[r.status] || STATUS_META["Allocated"];

  return (
    <div
      ref={setNodeRef}
      style={style}
      className="grid gap-2 px-4 h-12 items-center border-b border-slate-100 hover:bg-slate-50/40 group"
      data-testid={`content-row-${r.id}`}
    >
      <div className="flex justify-center">
        <button {...attributes} {...listeners} className="opacity-0 group-hover:opacity-100 cursor-grab text-slate-400 hover:text-slate-700" data-testid={`content-drag-${r.id}`}>
          <GripVertical className="h-4 w-4" />
        </button>
      </div>
      <Input
        defaultValue={r.task}
        onBlur={(e) => { const v = e.target.value.trim(); if (v !== r.task) onUpdate(r.id, { task: v }); }}
        placeholder="Task"
        className="h-8 text-sm"
        data-testid={`content-task-${r.id}`}
      />
      <Input
        defaultValue={r.sub_task}
        onBlur={(e) => { const v = e.target.value.trim(); if (v !== r.sub_task) onUpdate(r.id, { sub_task: v }); }}
        placeholder="Sub-task"
        className="h-8 text-sm"
        data-testid={`content-subtask-${r.id}`}
      />
      <div>
        <ContentDateCell row={r} onUpdate={onUpdate} testid={`content-date-${r.id}`} />
      </div>
      <Select value={r.assignee || "__none__"} onValueChange={(v) => onUpdate(r.id, { assignee: v === "__none__" ? "" : v })}>
        <SelectTrigger className="h-8 text-xs" data-testid={`content-assignee-${r.id}`}>
          <SelectValue placeholder="Assign to" />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="__none__"><span className="text-slate-500">Unassigned</span></SelectItem>
          {members.map((m) => <SelectItem key={m.id} value={m.email}>{m.name}</SelectItem>)}
        </SelectContent>
      </Select>
      <Input
        defaultValue={r.remarks}
        key={r.remarks}
        onBlur={(e) => { const v = e.target.value.trim(); if (v !== r.remarks) onUpdate(r.id, { remarks: v }); }}
        placeholder="Add remark…"
        className="h-8 text-sm"
        data-testid={`content-remark-${r.id}`}
      />
      <Select value={r.status || "Allocated"} onValueChange={(v) => onUpdate(r.id, { status: v })}>
        <SelectTrigger className={`h-8 text-xs border-0 font-medium ${sMeta.bg} ${sMeta.text}`} data-testid={`content-status-${r.id}`}>
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {CONTENT_STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
        </SelectContent>
      </Select>
      <div className="flex items-center justify-end gap-1">
        <button
          onClick={() => onOpenLinks(r)}
          className="relative h-8 w-8 rounded-md hover:bg-sky-50 text-slate-500 hover:text-blue-600 flex items-center justify-center"
          title="Links"
          data-testid={`content-links-${r.id}`}
        >
          <Link2 className="h-4 w-4" />
          {(r.references || []).length > 0 && (
            <span className="absolute -top-1 -right-1 h-4 min-w-[16px] px-1 rounded-full bg-blue-600 text-white text-[10px] font-bold flex items-center justify-center">
              {(r.references || []).length}
            </span>
          )}
        </button>
        <button onClick={() => onDuplicate(r)} className="h-8 w-8 rounded-md hover:bg-slate-100 text-slate-500 hover:text-slate-800 flex items-center justify-center" title="Duplicate" data-testid={`content-duplicate-${r.id}`}>
          <Copy className="h-4 w-4" />
        </button>
        <button onClick={() => onDelete(r)} className="h-8 w-8 rounded-md hover:bg-rose-50 text-slate-400 hover:text-rose-600 flex items-center justify-center" title="Delete" data-testid={`content-delete-${r.id}`}>
          <Trash2 className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
}

export default function ContentTable() {
  const [rows, setRows] = useState([]);
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [linksOpen, setLinksOpen] = useState(false);
  const [activeRow, setActiveRow] = useState(null);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  const load = async () => {
    try {
      const { data } = await api.get("/content-items");
      setRows(data);
    } catch { toast.error("Failed to load content"); }
    finally { setLoading(false); }
  };
  const loadMembers = async () => {
    try { const { data } = await api.get("/members"); setMembers(data); } catch { /* ignore */ }
  };

  useEffect(() => { load(); loadMembers(); }, []);

  const handleCreate = async () => {
    try {
      const { data } = await api.post("/content-items", {});
      setRows((prev) => [...prev, data]);
    } catch { toast.error("Failed to add row"); }
  };
  const handleUpdate = async (id, patch) => {
    setRows((prev) => prev.map((r) => (r.id === id ? { ...r, ...patch } : r)));
    try {
      const { data } = await api.patch(`/content-items/${id}`, patch);
      setRows((prev) => prev.map((r) => (r.id === id ? data : r)));
      return data;
    } catch { toast.error("Update failed"); load(); }
  };
  const handleDelete = async (r) => {
    if (!window.confirm(`Delete "${r.task || "this row"}"?`)) return;
    try {
      await api.delete(`/content-items/${r.id}`);
      setRows((prev) => prev.filter((x) => x.id !== r.id));
      toast.success("Deleted");
    } catch { toast.error("Delete failed"); }
  };
  const handleDuplicate = async (r) => {
    try { await api.post(`/content-items/${r.id}/duplicate`); await load(); toast.success("Duplicated"); }
    catch { toast.error("Duplicate failed"); }
  };

  const handleDragEnd = async (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIndex = rows.findIndex((r) => r.id === active.id);
    const newIndex = rows.findIndex((r) => r.id === over.id);
    const next = arrayMove(rows, oldIndex, newIndex);
    setRows(next);
    try { await api.post("/content-items/reorder", { order: next.map((r) => r.id) }); }
    catch { toast.error("Reorder failed"); load(); }
  };

  return (
    <div className="space-y-4" data-testid="content-table">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h3 className="font-display text-lg font-semibold">Content</h3>
          <p className="text-xs text-slate-500">Track content tasks, owners, timelines and status.</p>
        </div>
        <Button onClick={handleCreate} className="btn-brand h-10 rounded-xl" data-testid="content-new-row">
          <Plus className="h-4 w-4 mr-1" /> Add row
        </Button>
      </div>

      <div className="bg-white rounded-2xl border border-slate-200 overflow-x-auto">
        <div style={{ minWidth: 1150 }}>
          <div
            className="grid gap-2 text-[11px] font-bold uppercase tracking-[0.12em] text-slate-700 px-4 h-11 items-center bg-slate-100/70 border-b border-slate-200 whitespace-nowrap"
            style={{ gridTemplateColumns: GRID }}
          >
            <div></div>
            <div>Task</div>
            <div>Sub-task</div>
            <div>Timeline</div>
            <div>Assignee</div>
            <div>Remarks</div>
            <div>Status</div>
            <div className="text-right pr-2">Actions</div>
          </div>

          {loading ? (
            <div className="px-4 py-10 text-center text-sm text-slate-400">Loading…</div>
          ) : rows.length === 0 ? (
            <div className="px-4 py-10 text-center text-sm text-slate-400" data-testid="content-empty">
              No content yet — click "Add row"
            </div>
          ) : (
            <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
              <SortableContext items={rows.map((r) => r.id)} strategy={verticalListSortingStrategy}>
                {rows.map((r) => (
                  <ContentRow
                    key={r.id}
                    r={r}
                    members={members}
                    onUpdate={handleUpdate}
                    onDuplicate={handleDuplicate}
                    onDelete={handleDelete}
                    onOpenLinks={(row) => { setActiveRow(row); setLinksOpen(true); }}
                  />
                ))}
              </SortableContext>
            </DndContext>
          )}
        </div>
      </div>

      <ContentLinksDialog
        open={linksOpen}
        onOpenChange={setLinksOpen}
        item={activeRow}
        onSave={(patch) => handleUpdate(activeRow.id, patch)}
      />
    </div>
  );
}
